/* ==========================================================================
   BEHIND - SWIPEABLE PRODUCT CARD IMAGES
   Custom code for Behind. Kept separate from the Prestige core files so the
   theme stays easy to update.

   Loaded by sections/main-collection.liquid and
   sections/featured-collections.liquid, only when the swipe gallery is
   switched on for that section in the Theme Editor.

   The markup is rendered by snippets/product-card.liquid:

     <swipe-gallery class="custom-swipe" data-model-skip="2">
       <div class="custom-swipe__viewport">
         <a class="product-card__media custom-swipe__track"> ...slides... </a>
       </div>
       <div class="custom-swipe__rail"><span class="custom-swipe__thumb"></span></div>
     </swipe-gallery>

   Scrolling itself is done by the browser (overflow-x with scroll snapping),
   so swiping stays smooth and vertical page scrolling keeps working. This
   file only moves the indicator, loads one image ahead and adds mouse
   dragging for desktop.

   ON IMAGE LOADING, AND WHY IT IS CAREFUL
   A collection page can hold fifty product cards and a card can hold six
   pictures. Waking all of those up at once is a quick way to run a phone out
   of memory, so the images after the first stay lazy and only the single
   picture next to the one on screen is ever promoted, and only once that
   gallery has really been scrolled sideways. Touching a card while scrolling
   the page down must never start a download.

   In the Model view the first slides are hidden by CSS, so the gallery starts
   on the third product image. data-model-skip says how many slides that is,
   which is what the indicator counts with.

   Because this is a custom element, every gallery starts itself as soon as it
   is inserted in the page. That is what makes it keep working after
   filtering, sorting or loading more products, when Shopify replaces the
   product grid with freshly rendered HTML.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('swipe-gallery')) {
    return;
  }

  /* How far the mouse may travel before a press counts as a drag instead of
     a click on the product. */
  var DRAG_THRESHOLD = 8;

  /* Every gallery on the page, so one observer can tell them all when the
     Model / Product view changes. */
  var galleries = [];
  var viewObserver = null;

  function watchViewChanges() {
    if (viewObserver || !window.MutationObserver) {
      return;
    }

    viewObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var changed = mutations[i].target;

        for (var j = 0; j < galleries.length; j++) {
          if (changed.contains(galleries[j])) {
            galleries[j].refresh();
          }
        }
      }
    });

    viewObserver.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-collection-view']
    });
  }

  function SwipeGallery() {
    return Reflect.construct(HTMLElement, [], SwipeGallery);
  }

  SwipeGallery.prototype = Object.create(HTMLElement.prototype);
  SwipeGallery.prototype.constructor = SwipeGallery;
  Object.setPrototypeOf(SwipeGallery, HTMLElement);

  SwipeGallery.prototype.connectedCallback = function () {
    var self = this;

    this.track = this.querySelector('.custom-swipe__track');
    this.rail = this.querySelector('.custom-swipe__rail');
    this.thumb = this.querySelector('.custom-swipe__thumb');

    if (!this.track || this.isReady) {
      return;
    }

    this.isReady = true;
    this.slides = this.track.querySelectorAll('.custom-swipe__slide');
    this.totalSlides = this.slides.length || 1;
    this.modelSkip = parseInt(this.getAttribute('data-model-skip'), 10) || 0;
    this.slideCount = this.visibleCount();
    this.frame = null;
    this.pointerId = null;
    this.dragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;

    /* Which image was showing when the finger or mouse went down. A click is
       only swallowed when the gallery really moved on to another image, so a
       plain tap always opens the product page. */
    this.pressIndex = 0;
    this.pressDragged = false;

    this.onScroll = function () {
      self.requestUpdate();
    };

    this.onPointerDown = function (event) {
      self.pointerDown(event);
    };

    this.onPointerMove = function (event) {
      self.pointerMove(event);
    };

    this.onPointerUp = function (event) {
      self.pointerUp(event);
    };

    this.onTrackClick = function (event) {
      self.trackClick(event);
    };

    this.onDragStart = function (event) {
      event.preventDefault();
    };

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('lostpointercapture', this.onPointerUp);
    this.track.addEventListener('dragstart', this.onDragStart);

    /* Capture phase, so a swipe is stopped before the link reacts to it. */
    this.track.addEventListener('click', this.onTrackClick, true);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(function () {
        self.update();
      });

      this.resizeObserver.observe(this.track);
    }

    galleries.push(this);
    watchViewChanges();

    this.update();
  };

  SwipeGallery.prototype.disconnectedCallback = function () {
    var index = galleries.indexOf(this);

    if (index > -1) {
      galleries.splice(index, 1);
    }

    if (!this.isReady || !this.track) {
      return;
    }

    this.track.removeEventListener('scroll', this.onScroll);
    this.track.removeEventListener('pointerdown', this.onPointerDown);
    this.track.removeEventListener('pointermove', this.onPointerMove);
    this.track.removeEventListener('pointerup', this.onPointerUp);
    this.track.removeEventListener('pointercancel', this.onPointerUp);
    this.track.removeEventListener('lostpointercapture', this.onPointerUp);
    this.track.removeEventListener('dragstart', this.onDragStart);
    this.track.removeEventListener('click', this.onTrackClick, true);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    this.dragging = false;
    this.pointerId = null;
    this.isReady = false;
  };

  /* ------------------------------------------------------------------------
     MODEL VIEW
     ------------------------------------------------------------------------ */

  SwipeGallery.prototype.inModelView = function () {
    if (!this.closest) {
      return false;
    }

    return this.closest('[data-collection-view="model"]') !== null;
  };

  /* How many images are really in the strip right now. In the Model view the
     skipped ones are display:none, so they count for nothing. */
  SwipeGallery.prototype.visibleCount = function () {
    if (this.modelSkip > 0 && this.inModelView()) {
      var left = this.totalSlides - this.modelSkip;
      return left > 0 ? left : 1;
    }

    return this.totalSlides;
  };

  /* Called when the shopper switches between Model and Product. The strip
     changes length, so it goes back to its first image and the indicator is
     measured again. */
  SwipeGallery.prototype.refresh = function () {
    if (!this.isReady || !this.track) {
      return;
    }

    this.slideCount = this.visibleCount();
    this.track.scrollLeft = 0;
    this.update();
  };

  SwipeGallery.prototype.currentIndex = function () {
    var width = this.track.clientWidth;

    if (width <= 0) {
      return 0;
    }

    return Math.round(Math.abs(this.track.scrollLeft) / width);
  };

  /* ------------------------------------------------------------------------
     IMAGES
     Only ever one picture ahead of the one being looked at, and only after
     this gallery has really been scrolled sideways.
     ------------------------------------------------------------------------ */

  SwipeGallery.prototype.preloadNextImage = function () {
    if (Math.abs(this.track.scrollLeft) < 1) {
      return;
    }

    var next = this.slides[this.currentIndex() + 1];

    if (!next) {
      return;
    }

    var image = next.querySelector('img[loading="lazy"]');

    if (image) {
      image.setAttribute('loading', 'eager');
    }
  };

  /* ------------------------------------------------------------------------
     INDICATOR
     ------------------------------------------------------------------------ */

  SwipeGallery.prototype.requestUpdate = function () {
    var self = this;

    if (this.frame) {
      return;
    }

    this.frame = window.requestAnimationFrame(function () {
      self.frame = null;
      self.update();
    });
  };

  SwipeGallery.prototype.update = function () {
    if (!this.track) {
      return;
    }

    this.preloadNextImage();

    if (!this.rail || !this.thumb) {
      return;
    }

    var railWidth = this.rail.clientWidth;
    var scrollWidth = this.track.scrollWidth;

    if (railWidth <= 0 || scrollWidth <= 0) {
      return;
    }

    /* The thumb takes the same share of the rail as one image takes of the
       whole strip, so it moves proportionally with the swipe. */
    var thumbWidth = railWidth / this.slideCount;
    var offset = (Math.abs(this.track.scrollLeft) / scrollWidth) * railWidth;
    var maxOffset = railWidth - thumbWidth;

    if (offset > maxOffset) {
      offset = maxOffset;
    }

    if (offset < 0) {
      offset = 0;
    }

    this.thumb.style.width = thumbWidth + 'px';
    this.thumb.style.transform = 'translateX(' + offset + 'px)';
  };

  /* ------------------------------------------------------------------------
     DRAGGING WITH A MOUSE (DESKTOP)
     On touch the browser does this itself, so this only runs for a mouse.
     ------------------------------------------------------------------------ */

  SwipeGallery.prototype.pointerDown = function (event) {
    this.startX = event.clientX;
    this.pressIndex = this.currentIndex();
    this.pressDragged = false;

    if (event.pointerType !== 'mouse' || event.button !== 0) {
      /* A finger on a card is almost always the page being scrolled down, so
         nothing at all happens here. */
      return;
    }

    /* On the home page the cards sit in Prestige's own draggable carousel.
       Dragging on a card belongs to the card's images, the carousel keeps its
       arrows and its own empty space. */
    event.stopPropagation();

    this.pointerId = event.pointerId;
    this.dragging = true;
    this.startScrollLeft = this.track.scrollLeft;
    this.track.classList.add('is-dragging');

    /* Pointer capture keeps the drag alive when the cursor leaves the card,
       and guarantees the matching pointerup. */
    if (this.track.setPointerCapture) {
      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (error) {
        /* Some browsers refuse capture on a synthetic pointer, never fatal. */
      }
    }
  };

  SwipeGallery.prototype.pointerMove = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) {
      return;
    }

    /* The button was released somewhere we could not see, so end the drag. */
    if (event.buttons === 0) {
      this.pointerUp(event);
      return;
    }

    var deltaX = event.clientX - this.startX;

    if (Math.abs(deltaX) > DRAG_THRESHOLD) {
      this.pressDragged = true;
    }

    if (Math.abs(deltaX) < 1) {
      return;
    }

    event.preventDefault();
    this.track.scrollLeft = this.startScrollLeft - deltaX;
  };

  SwipeGallery.prototype.pointerUp = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) {
      return;
    }

    this.dragging = false;
    this.track.classList.remove('is-dragging');

    if (this.track.hasPointerCapture && this.track.hasPointerCapture(this.pointerId)) {
      this.track.releasePointerCapture(this.pointerId);
    }

    this.pointerId = null;
    this.snap();
  };

  SwipeGallery.prototype.snap = function () {
    var width = this.track.clientWidth;

    if (width <= 0) {
      return;
    }

    /* scrollLeft is negative in right to left languages, so the sign is kept
       and only the distance is rounded to the nearest image. */
    var direction = this.track.scrollLeft < 0 ? -1 : 1;
    var index = Math.round(Math.abs(this.track.scrollLeft) / width);
    var lastIndex = this.slideCount - 1;

    if (index < 0) {
      index = 0;
    }

    if (index > lastIndex) {
      index = lastIndex;
    }

    var target = index * width * direction;

    if (this.track.scrollTo) {
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = target;
    }
  };

  /* A swipe must never open the product page, a tap always must. The decision
     is made on whether another image is now showing, or whether the mouse was
     really dragged, so a sloppy tap and a tap during a snap both still open
     the product. */
  SwipeGallery.prototype.trackClick = function (event) {
    if (!this.pressDragged && this.currentIndex() === this.pressIndex) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.pressDragged = false;
  };

  window.customElements.define('swipe-gallery', SwipeGallery);
})();
